import type { ColumnFiltersState } from "@tanstack/react-table";
import { z } from "zod";

export const ecomSearchSchema = z.object({
	dateFrom: z.string().catch("").optional(),
	dateTo: z.string().catch("").optional(),
	q: z.union([z.string(), z.number()]).transform(String).catch("").optional(),
	columnFilters: z.string().catch("").optional(),
	sortBy: z.string().catch("name").optional(),
	sortDir: z.enum(["asc", "desc"]).catch("asc").optional(),
	panelSku: z.union([z.string(), z.number()]).transform(String).catch("").optional(),
	panelMode: z.enum(["stock", "history"]).catch("stock").optional(),
	panelFilters: z.string().catch("").optional(),
	panelSortBy: z.string().catch("purchaseDate").optional(),
	panelSortDir: z.enum(["asc", "desc"]).catch("desc").optional(),
});
export type EcomSearch = z.infer<typeof ecomSearchSchema>;

export function parseEcomFilters(raw?: string): ColumnFiltersState {
	try {
		const value: unknown = JSON.parse(raw || "[]");
		// zod 4 wymaga klucza dla z.unknown(); filtr bez `value` (JSON.stringify
		// gubi undefined) wyzerowalby wszystkie filtry, czego zod 3 nie robil.
		return z
			.array(z.object({ id: z.string(), value: z.unknown().optional() }))
			.parse(value) as ColumnFiltersState;
	} catch {
		return [];
	}
}

export function ecomDates(now = new Date()) {
	const today = new Intl.DateTimeFormat("en-CA", {
		timeZone: "Europe/Warsaw",
		year: "numeric",
		month: "2-digit",
		day: "2-digit",
	}).format(now);
	const date = new Date(`${today}T12:00:00Z`);
	const iso = (d: Date) => d.toISOString().slice(0, 10);
	const max = new Date(date);
	max.setUTCDate(max.getUTCDate() - 1);
	const from = new Date(date);
	from.setUTCDate(from.getUTCDate() - 30);
	const min = new Date(date);
	min.setUTCFullYear(min.getUTCFullYear() - 2);
	return { dateFrom: iso(from), dateTo: iso(max), minDate: iso(min), maxDate: iso(max) };
}

export function ecomDateError(from: string, to: string, min: string, max: string) {
	const valid = (s: string) =>
		/^\d{4}-\d{2}-\d{2}$/.test(s) &&
		!Number.isNaN(Date.parse(`${s}T12:00:00Z`)) &&
		new Date(`${s}T12:00:00Z`).toISOString().startsWith(s);
	if (!valid(from) || !valid(to)) return "Wybierz poprawne daty początku i końca.";
	if (from > to) return "Początek okresu musi przypadać przed jego końcem.";
	if (from < min || to > max) return `Dostępne pełne dni: ${min} – ${max}. Zmień zakres.`;
	return null;
}

export const mappingOptions = [
	{ value: "matched", label: "Powiązany" },
	{ value: "missing_bloz", label: "Brak BLOZ" },
	{ value: "duplicate_bloz", label: "Kolizja BLOZ" },
	{ value: "ambiguous_source", label: "Niejednoznaczne źródło Kamsoft" },
	{ value: "unmatched_bloz", label: "Brak w Kamsoft" },
];
export const rotationOptions = [
	{ value: "no_stock", label: "Brak zapasu" },
	{ value: "no_sales", label: "Zapas bez sprzedaży 30 dni" },
	{ value: "up_to_60", label: "Do 60 dni" },
	{ value: "61_120", label: "Ponad 60–120 dni" },
	{ value: "121_180", label: "Ponad 120–180 dni" },
	{ value: "181_240", label: "Ponad 180–240 dni" },
	{ value: "241_300", label: "Ponad 240–300 dni" },
	{ value: "over_300", label: "Ponad 300 dni" },
];
// Wartosci redirect_type PrestaShop 1.7.8; "none" to pusty typ, czyli brak przekierowania.
export const redirectOptions = [
	{ value: "none", label: "Nieustawione (404)" },
	{ value: "404", label: "404" },
	{ value: "301-category", label: "301 do kategorii" },
	{ value: "302-category", label: "302 do kategorii" },
	{ value: "301-product", label: "301 do produktu" },
	{ value: "302-product", label: "302 do produktu" },
];
export const booleanOptions = [
	{ value: "true", label: "Tak" },
	{ value: "false", label: "Nie" },
];
export const bestSourceOptions = [
	{ value: "neuca", label: "Neuca" },
	{ value: "pgf", label: "PGF" },
	{ value: "cefarm", label: "Cefarm" },
	{ value: "hurtap", label: "Hurtap" },
	{ value: "farmacol", label: "Farmacol" },
];
export const producerSourceOptions = [
	{ value: "shop", label: "Sklep" },
	{ value: "bloz_ean", label: "BLOZ (zgodny EAN)" },
	{ value: "bloz", label: "BLOZ" },
];

export const ecomLabels = {
	name: "Nazwa produktu",
	bloz07: "BLOZ",
	salesRank: "Miejsce sprzedaży",
	top1000: "TOP 1000",
	sku: "SKU",
	ean: "EAN",
	producerId: "ID producenta sklepu",
	farmasterProducerId: "ID producenta Farmaster",
	producer: "Producent",
	producerSource: "Źródło producenta",
	active: "Aktywna karta",
	shopRedirect: "Przekierowanie karty",
	createdAt: "Data utworzenia karty",
	mappingStatus: "Powiązanie BLOZ",
	shopPriceNet: "Cena sklepu netto [zł]",
	shopStock: "Dostępność sklepu [szt.]",
	bestPrice: "Najniższa cena zakupu [zł]",
	bestSource: "Najtańsze źródło",
	hurtNeuca: "Neuca po rabatach [zł]",
	hurtPgf: "PGF po rabatach [zł]",
	hurtCefarm: "Cefarm po rabatach [zł]",
	hurtHurtap: "Hurtap po rabatach [zł]",
	hurtFarmacol: "Farmacol po rabatach [zł]",
	vat: "VAT [%]",
	salesQty: "Sprzedaż w okresie [szt.]",
	salesNet: "Przychód netto w okresie [zł]",
	costNet: "Koszt sprzedanych towarów w okresie [zł]",
	marginPln: "Marża front w okresie [zł]",
	marginPct: "Marża front [%]",
	sales30Qty: "Sprzedaż 30 dni [szt.]",
	sales90Qty: "Sprzedaż 90 dni [szt.]",
	sales90Net: "Przychód netto 90 dni [zł]",
	stock: "Zapas Kamsoft [szt.]",
	stockNet: "Zapas Kamsoft netto [zł]",
	stockAvailable: "Dostępny zapas Kamsoft [szt.]",
	blockedStock: "Zablokowany zapas [szt.]",
	blockedStockNet: "Zablokowany zapas netto [zł]",
	purchaseQty: "Zakupy w okresie [szt.]",
	purchaseNet: "Zakupy w okresie netto [zł]",
	lastPurchaseDate: "Ostatnie przyjęcie",
	lastPurchasePrice: "Ostatnia cena zakupu netto [zł]",
	lastPurchaseInvoice: "Ostatnia FV",
	lastPurchaseIssueDate: "Data wystawienia ostatniej FV",
	previousPurchaseDate1: "Poprzednie przyjęcie 1",
	previousPurchasePrice1: "Poprzednia cena 1 netto [zł]",
	previousPurchaseInvoice1: "Poprzednia FV 1",
	previousPurchaseIssueDate1: "Data wystawienia poprzedniej FV 1",
	previousPurchaseDate2: "Poprzednie przyjęcie 2",
	previousPurchasePrice2: "Poprzednia cena 2 netto [zł]",
	previousPurchaseInvoice2: "Poprzednia FV 2",
	previousPurchaseIssueDate2: "Data wystawienia poprzedniej FV 2",
	expiryDate: "Najkrótsza ważność",
	lastSaleDate: "Ostatnia sprzedaż",
	inContract: "W kontrakcie",
	rotationDays: "Rotacja [dni]",
	rotationSegment: "Segment rotacji",
};
export const ecomDefaultVisibility = Object.fromEntries(
	Object.keys(ecomLabels).map((id) => [
		id,
		[
			"name",
			"bloz07",
			"salesRank",
			"top1000",
			"shopPriceNet",
			"shopStock",
			"bestPrice",
			"bestSource",
			"hurtNeuca",
			"hurtPgf",
			"hurtCefarm",
			"hurtHurtap",
			"hurtFarmacol",
			"salesQty",
			"salesNet",
			"marginPct",
			"stock",
			"purchaseQty",
			"purchaseNet",
			"rotationDays",
		].includes(id),
	]),
);
export type EcomRow = Record<keyof typeof ecomLabels, string | number | boolean | null> & {
	sku: string;
	name: string;
	productName?: string | null;
};
export type EcomResponse = {
	rows: EcomRow[];
	totalCount: number;
	summary: Record<string, number | null>;
	meta: {
		dateFrom: string;
		dateTo: string;
		minDate: string;
		maxDate: string;
		catalogSyncedAt: string | null;
		stockSyncedAt: string | null;
		expirySnapshotDate: string | null;
		deliveryMinDate: string;
		freshness: {
			sources: {
				name: string;
				kind: string;
				snapshotStartedAt: string | null;
				refreshedAt: string | null;
			}[];
			oldestSnapshotAt: string | null;
		};
	};
};
export const ecomSumColumns = [
	"salesQty",
	"salesNet",
	"costNet",
	"marginPln",
	"sales30Qty",
	"sales90Qty",
	"sales90Net",
	"stock",
	"stockNet",
	"stockAvailable",
	"blockedStock",
	"blockedStockNet",
	"purchaseQty",
	"purchaseNet",
	"shopStock",
];
export const ecomNumericColumns = new Set([
	...ecomSumColumns,
	"salesRank",
	"vat",
	"marginPct",
	"shopPriceNet",
	"lastPurchasePrice",
	"previousPurchasePrice1",
	"previousPurchasePrice2",
	"rotationDays",
	"bestPrice",
	"hurtNeuca",
	"hurtPgf",
	"hurtCefarm",
	"hurtHurtap",
	"hurtFarmacol",
]);
/** Punkty procentowe z backendu (`vatsp`, `100 * margin / net`) — eksport jako %. */
export const ecomPercentColumns = new Set(["vat", "marginPct"]);
